import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResultCard } from "@/components/calculator/ResultCard";
import { PresetButton } from "@/components/calculator/PresetButton";
import { Zap, Battery, Car, Sun } from "lucide-react";

// Debounce helper
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

export const DCPowerTab = ({ onSaveHistory }) => {
  const [voltage, setVoltage] = useState(12);
  const [current, setCurrent] = useState(10);
  const [efficiency, setEfficiency] = useState(95);
  const [powerUnit, setPowerUnit] = useState("W");
  
  const debouncedVoltage = useDebounce(voltage, 200);
  const debouncedCurrent = useDebounce(current, 200);
  const debouncedEfficiency = useDebounce(efficiency, 200);

  // Calculations
  const outputPower = debouncedVoltage * debouncedCurrent * (debouncedEfficiency / 100);
  const inputPower = outputPower / (debouncedEfficiency / 100);

  // Unit conversion
  const formatPower = useCallback((watts) => {
    switch (powerUnit) {
      case "kW":
        return (watts / 1000).toFixed(3);
      case "MW":
        return (watts / 1000000).toFixed(6);
      default:
        return watts.toFixed(2);
    }
  }, [powerUnit]);

  // Save to history
  const handleSave = useCallback(() => {
    onSaveHistory({
      type: "DC Power",
      inputs: {
        voltage: `${debouncedVoltage} V`,
        current: `${debouncedCurrent} A`,
        efficiency: `${debouncedEfficiency}%`
      },
      outputs: {
        outputPower: `${formatPower(outputPower)} ${powerUnit}`,
        inputPower: `${formatPower(inputPower)} ${powerUnit}`
      }
    });
  }, [debouncedVoltage, debouncedCurrent, debouncedEfficiency, outputPower, inputPower, powerUnit, formatPower, onSaveHistory]);

  // Presets
  const presets = [
    { name: "Buck Converter", voltage: 12, current: 80, efficiency: 95, icon: Battery },
    { name: "Solar Panel", voltage: 48, current: 15, efficiency: 92, icon: Sun },
    { name: "EV Charger", voltage: 400, current: 50, efficiency: 97, icon: Car },
  ];

  const applyPreset = (preset) => {
    setVoltage(preset.voltage);
    setCurrent(preset.current);
    setEfficiency(preset.efficiency);
  };

  return (
    <div className="space-y-6">
      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => (
          <PresetButton
            key={preset.name}
            label={preset.name}
            icon={preset.icon}
            onClick={() => applyPreset(preset)}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-6">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Input Parameters
          </h3>

          {/* Voltage */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="dc-voltage" className="text-sm font-medium">
                Voltage (V)
              </Label>
              <div className="flex gap-1">
                {[12, 24, 48].map((v) => (
                  <Button
                    key={v}
                    variant={voltage === v ? "default" : "outline"}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setVoltage(v)}
                  >
                    {v}V
                  </Button>
                ))}
              </div>
            </div>
            <Input
              id="dc-voltage"
              type="number"
              min="0"
              max="1000"
              value={voltage}
              onChange={(e) => setVoltage(Number(e.target.value) || 0)}
              className="bg-secondary/50 border-border focus:border-primary"
            />
            <p className="text-xs text-muted-foreground">Range: 0 - 1000 V</p>
          </div>

          {/* Current */}
          <div className="space-y-2">
            <Label htmlFor="dc-current" className="text-sm font-medium">
              Current (A)
            </Label>
            <Input
              id="dc-current"
              type="number"
              min="0"
              max="10000"
              value={current}
              onChange={(e) => setCurrent(Number(e.target.value) || 0)}
              className="bg-secondary/50 border-border focus:border-primary"
            />
            <p className="text-xs text-muted-foreground">Range: 0 - 10,000 A</p>
          </div>

          {/* Efficiency */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Efficiency
              </Label>
              <span className="text-sm font-mono text-primary">{efficiency}%</span>
            </div>
            <Slider
              value={[efficiency]}
              onValueChange={(value) => setEfficiency(value[0])}
              min={50}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Unit Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Power Unit</Label>
            <Select value={powerUnit} onValueChange={setPowerUnit}>
              <SelectTrigger className="bg-secondary/50 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="W">Watts (W)</SelectItem>
                <SelectItem value="kW">Kilowatts (kW)</SelectItem>
                <SelectItem value="MW">Megawatts (MW)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Results
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              className="text-xs"
            >
              Save to History
            </Button>
          </div>

          <div className="space-y-4">
            <ResultCard
              label="Output Power"
              value={formatPower(outputPower)}
              unit={powerUnit}
              color="primary"
              formula="P_out = V × I × (η/100)"
            />
            
            <ResultCard
              label="Input Power"
              value={formatPower(inputPower)}
              unit={powerUnit}
              color="accent"
              formula="P_in = P_out / (η/100)"
            />
          </div>

          {/* Example */}
          <div className="p-4 rounded-lg bg-secondary/30 border border-border">
            <div className="flex items-start gap-3">
              <Zap className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Example: Buck Converter</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Vin=12V, Vout=1.2V, Iout=80A, η=95%
                  <br />
                  → P_out = 12 × 80 × 0.95 = <span className="text-primary font-mono">912W</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
